/**
 * Scenario Economy View component for viewing scenario economics
 */
import { ArrowBack } from '@mui/icons-material';
import {
    Box,
    Button,
    Card,
    CardContent,
    Chip,
    Grid,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    Typography,
} from '@mui/material';
import React from 'react';
import { ScenarioResponse } from '../types/scenario';
import { ScenarioEconomy } from '../types/scenarioEconomy';

interface ScenarioEconomyViewProps {
  scenarioId: string;
  economy: ScenarioEconomy | null;
  scenario: ScenarioResponse;
  onBack: () => void;
}

export const ScenarioEconomyView: React.FC<ScenarioEconomyViewProps> = ({
  scenarioId,
  economy,
  scenario,
  onBack,
}) => {
  const formatCurrency = (value: number | null | undefined) => {
    if (value === null || value === undefined) return 'Not set';
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD',
      minimumFractionDigits: 0,
      maximumFractionDigits: 0,
    }).format(value);
  };

  const formatPercentage = (value: number | null | undefined) => {
    if (value === null || value === undefined) return 'Not set';
    return `${(value * 100).toFixed(1)}%`;
  };

  const formatNumber = (value: number | null | undefined, decimals: number = 2) => {
    if (value === null || value === undefined) return 'Not set';
    return value.toLocaleString(undefined, {
      minimumFractionDigits: decimals,
      maximumFractionDigits: decimals,
    });
  };

  const getAttributeValue = (attribute: any) => {
    if (attribute.string_value !== null && attribute.string_value !== undefined) {
      return attribute.string_value;
    }
    if (attribute.float_value !== null && attribute.float_value !== undefined) {
      return formatNumber(attribute.float_value);
    }
    if (attribute.integer_value !== null && attribute.integer_value !== undefined) {
      return attribute.integer_value.toLocaleString();
    }
    return 'Not set';
  };

  const getAttributeUnit = (attribute: any) => {
    return attribute.economy_attribute?.unit || '';
  };

  const getAttributeName = (attribute: any) => {
    return attribute.economy_attribute?.name || 'Unknown Attribute';
  };

  const getAttributeCategory = (attribute: any) => {
    return attribute.economy_attribute?.category || 'General';
  };

  const groupAttributesByCategory = () => {
    if (!economy?.attributes) return {};

    const grouped: Record<string, any[]> = {};
    economy.attributes.forEach(attr => {
      const category = getAttributeCategory(attr);
      if (!grouped[category]) {
        grouped[category] = [];
      }
      grouped[category].push(attr);
    });

    return grouped;
  };

  const groupedAttributes = groupAttributesByCategory();

  return (
    <Box>
      {/* Back Button */}
      <Box sx={{ mb: 3 }}>
        <Button
          startIcon={<ArrowBack />}
          onClick={onBack}
          variant="outlined"
          sx={{ mb: 2 }}
        >
          Back to Scenarios
        </Button>
      </Box>

      {/* Summary Cards */}
      <Grid container spacing={3} sx={{ mb: 3 }}>
        <Grid item xs={12} sm={6} md={3}>
          <Card>
            <CardContent>
              <Typography variant="h6" color="primary">
                {economy?.attributes?.length || 0}
              </Typography>
              <Typography variant="body2" color="text.secondary">
                Economic Parameters
              </Typography>
            </CardContent>
          </Card>
        </Grid>

        <Grid item xs={12} sm={6} md={3}>
          <Card>
            <CardContent>
              <Typography variant="h6" color="primary">
                {economy?.economy_template?.name ? 'Yes' : 'No'}
              </Typography>
              <Typography variant="body2" color="text.secondary">
                Template Used
              </Typography>
            </CardContent>
          </Card>
        </Grid>

        <Grid item xs={12} sm={6} md={3}>
          <Card>
            <CardContent>
              <Typography variant="h6" color="primary">
                {Object.keys(groupedAttributes).length}
              </Typography>
              <Typography variant="body2" color="text.secondary">
                Categories
              </Typography>
            </CardContent>
          </Card>
        </Grid>

        <Grid item xs={12} sm={6} md={3}>
          <Card>
            <CardContent>
              <Typography variant="h6" color="primary">
                {economy?.created_at ? new Date(economy.created_at).toLocaleDateString() : 'N/A'}
              </Typography>
              <Typography variant="body2" color="text.secondary">
                Last Updated
              </Typography>
            </CardContent>
          </Card>
        </Grid>
      </Grid>

      {/* Template Information */}
      {economy?.economy_template && (
        <Card sx={{ mb: 3 }}>
          <CardContent>
            <Typography variant="h6" gutterBottom>
              Economic Template
            </Typography>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
              <Typography variant="body1" fontWeight="medium">
                {economy.economy_template.name}
              </Typography>
              <Chip
                label={economy.economy_template.is_public ? 'Public' : 'Private'}
                color={economy.economy_template.is_public ? 'success' : 'default'}
                variant="outlined"
                size="small"
              />
            </Box>
            {economy.economy_template.description && (
              <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
                {economy.economy_template.description}
              </Typography>
            )}
          </CardContent>
        </Card>
      )}

      {/* Economic Parameters */}
      <Card>
        <CardContent>
          <Typography variant="h6" gutterBottom>
            Economic Parameters
          </Typography>

          {!economy || !economy.attributes || economy.attributes.length === 0 ? (
            <Box sx={{ textAlign: 'center', py: 4 }}>
              <Typography variant="body1" color="text.secondary">
                No economic parameters configured for this scenario.
              </Typography>
            </Box>
          ) : (
            <Box>
              {Object.entries(groupedAttributes).map(([category, attributes]) => (
                <Box key={category} sx={{ mb: 4 }}>
                  <Typography variant="h6" gutterBottom sx={{ color: 'primary.main' }}>
                    {category}
                  </Typography>
                  <TableContainer>
                    <Table size="small">
                      <TableHead>
                        <TableRow>
                          <TableCell>Parameter</TableCell>
                          <TableCell>Value</TableCell>
                          <TableCell>Unit</TableCell>
                        </TableRow>
                      </TableHead>
                      <TableBody>
                        {attributes.map((attr, index) => (
                          <TableRow key={index}>
                            <TableCell>
                              <Typography variant="body2" fontWeight="medium">
                                {getAttributeName(attr)}
                              </Typography>
                            </TableCell>
                            <TableCell>
                              <Typography variant="body2">
                                {getAttributeValue(attr)}
                              </Typography>
                            </TableCell>
                            <TableCell>
                              <Typography variant="body2" color="text.secondary">
                                {getAttributeUnit(attr)}
                              </Typography>
                            </TableCell>
                          </TableRow>
                        ))}
                      </TableBody>
                    </Table>
                  </TableContainer>
                </Box>
              ))}
            </Box>
          )}
        </CardContent>
      </Card>
    </Box>
  );
};
























